import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CubMascot from '../components/CubMascot.jsx';
import { ProgressRing, Confetti, Alert } from '../components/ui.jsx';
import * as celebrate from '../lib/celebrate.js';
import { useVoice } from '../lib/speech.js';

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

/**
 * One question at a time, then a results screen.
 * Grading happens on the server - `onSubmit` returns the graded result.
 */
export default function QuizPage({ page, lesson, onSubmit, onFinished, courseHref = '/learn' }) {
  const questions = page.questions;

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cheering, setCheering] = useState(celebrate.isOn);

  const voice = useVoice();

  // Never leave Cub congratulating an empty room.
  useEffect(() => () => celebrate.stop(), []);

  const current = questions[index];
  const chosen = answers[current?.id];
  const isLast = index === questions.length - 1;

  const choose = (i) => setAnswers((a) => ({ ...a, [current.id]: i }));

  const finish = async () => {
    setBusy(true);
    setError('');
    // Before the await, while the click that got us here still counts
    // as a user gesture - a browser will not start audio after it.
    celebrate.arm();
    try {
      const graded = await onSubmit(answers);
      setResult(graded);
      if (graded.passed) {
        onFinished?.(graded);
        celebrate.play({
          perfect: graded.score === graded.total,
          badges: graded.newBadges ?? [],
          voice,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const retry = () => {
    celebrate.stop();
    setAnswers({});
    setIndex(0);
    setResult(null);
    setError('');
  };

  /* ---------------- results ---------------- */
  if (result) {
    const pct = Math.round((result.score / result.total) * 100);
    const tone = result.passed ? (pct === 100 ? 'sun' : 'mint') : 'coral';

    return (
      <div className="slide quiz-results">
        <Confetti run={result.passed} />

        <div className="result-head tc">
          <CubMascot
            avatar={result.passed ? 'cub-mint' : 'cub-coral'}
            mood={result.passed ? 'cheer' : 'sad'}
            size={140}
            className={result.passed ? 'anim-float' : ''}
          />
          <h2>
            {pct === 100
              ? 'PERFECT! Every single one right! 🌟'
              : result.passed
                ? 'You passed! Brilliant work! 🎉'
                : 'So close — give it another try!'}
          </h2>

          <ProgressRing value={result.score} max={result.total} tone={tone} size={140}>
            <span style={{ fontSize: '1.9rem' }}>{result.score}/{result.total}</span>
            <span className="small faint" style={{ display: 'block' }}>{pct}%</span>
          </ProgressRing>

          {result.passed ? (
            <p className="muted mt-md">
              You earned <strong>{result.xpEarned} XP</strong>
              {result.saved ? '' : ' (preview mode — nothing was saved)'}.
            </p>
          ) : (
            <p className="muted mt-md">
              You need <strong>{lesson.passMark} out of {result.total}</strong> to pass. Read the
              answers below, then try again — there is no limit!
            </p>
          )}
        </div>

        {result.passed && (
          <button
            type="button"
            className={`quiz-cheer ${cheering ? 'is-on' : ''}`}
            onClick={() => {
              const next = !cheering;
              setCheering(next);
              celebrate.setOn(next);
              if (!next) celebrate.stop();
            }}
            aria-pressed={cheering}
            title={cheering ? 'Turn the celebration sound off' : 'Turn the celebration sound on'}
          >
            {cheering ? '🔊 Celebration on' : '🔇 Celebration off'}
          </button>
        )}

        {result.newBadges?.length > 0 && (
          <div className="badge-won">
            <h3>🏅 New badge{result.newBadges.length > 1 ? 's' : ''} unlocked!</h3>
            <div className="row center wrap gap-md">
              {result.newBadges.map((b) => (
                <div key={b.slug} className="badge-big anim-pop">
                  <span aria-hidden="true">{b.icon}</span>
                  <strong>{b.title}</strong>
                  <p className="small muted">{b.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <h3 className="mt-lg">Let us look at the answers</h3>
        <div className="review-list">
          {result.results.map((r, i) => (
            <div key={r.id} className={`review ${r.correct ? 'good' : 'bad'}`}>
              <div className="review-head">
                <span className="review-num">{i + 1}</span>
                <strong>{r.q}</strong>
                <span className={`pill ${r.correct ? 'pill-good' : 'pill-bad'}`}>
                  {r.correct ? '✓ Correct' : '✗ Not quite'}
                </span>
              </div>
              <ul className="review-options">
                {r.options.map((opt, oi) => (
                  <li
                    key={oi}
                    className={
                      oi === r.answer ? 'is-answer' : oi === r.chosen ? 'is-chosen-wrong' : ''
                    }
                  >
                    <span className="review-letter">{LETTERS[oi]}</span>
                    {opt}
                    {oi === r.answer && <span className="review-tag">correct answer</span>}
                    {oi === r.chosen && oi !== r.answer && <span className="review-tag">you picked</span>}
                  </li>
                ))}
              </ul>
              <p className="review-explain">💡 {r.explain}</p>
            </div>
          ))}
        </div>

        <div className="row center wrap gap-sm mt-lg">
          <button type="button" className="btn btn-ghost" onClick={retry}>
            🔄 Try the quiz again
          </button>
          <Link to={courseHref} className="btn">
            {result.passed ? 'Back to the course →' : 'Back to the course'}
          </Link>
        </div>
      </div>
    );
  }

  /* ---------------- questions ---------------- */
  return (
    <div className="slide quiz-slide">
      <div className="quiz-top">
        <h2 style={{ margin: 0 }}>{page.title}</h2>
        <span className="pill pill-tone t-grape">
          Question {index + 1} of {questions.length}
        </span>
      </div>

      {index === 0 && page.intro && <p className="slide-body">{page.intro}</p>}

      <div className="quiz-dots" aria-hidden="true">
        {questions.map((q, i) => (
          <span
            key={q.id}
            className={`quiz-dot ${i === index ? 'is-now' : ''} ${answers[q.id] !== undefined ? 'is-done' : ''}`}
          />
        ))}
      </div>

      {error && <Alert kind="bad" onDismiss={() => setError('')}>{error}</Alert>}

      <div className="quiz-question anim-pop" key={current.id}>
        <h3>{current.q}</h3>
        <div className="option-list">
          {current.options.map((opt, i) => (
            <button
              key={i}
              type="button"
              className={`option ${chosen === i ? 'is-chosen' : ''}`}
              onClick={() => choose(i)}
              aria-pressed={chosen === i}
            >
              <span className="option-letter">{LETTERS[i]}</span>
              <span>{opt}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="row spread wrap gap-sm mt-lg">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={index === 0}
        >
          ← Back
        </button>

        {isLast ? (
          <button
            type="button"
            className="btn btn-lg"
            onClick={finish}
            disabled={chosen === undefined || busy}
          >
            {busy ? 'Checking…' : 'Finish quiz 🎉'}
          </button>
        ) : (
          <button
            type="button"
            className="btn"
            onClick={() => setIndex((i) => i + 1)}
            disabled={chosen === undefined}
          >
            Next question →
          </button>
        )}
      </div>
    </div>
  );
}
